import React, { FunctionComponent, useState, useEffect } from 'react';

import classnames from 'classnames';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { Alert, Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { ButtonVariant } from 'core/util/Bootstrap';
import {
    formatTimezoneOffset,
    isValidISOstring,
    getTimezoneOffsetMap,
    convertDateTimeToCalendarDate,
    convertCalendarDateToDateTime,
} from 'social/common/util/Util';
import { SingleDateTimePicker } from 'social/publisher/date-time-picker/single-date-time-picker/SingleDateTimePicker';
import { PublishType, ScheduleDateObject, ScheduleDateTimeRange } from 'social/publisher/Publisher.types';
import { usePublisherDispatch } from 'social/publisher/PublisherContext';
import { getScheduleDateTimeDisplayObject } from 'social/publisher/util/Util';

import 'social/publisher/date-time-picker/DateTimePicker.scss';

interface Props {
    publishType: PublishType;
    scheduleDateTime: ScheduleDateObject[];
    isSingleLocationUser: boolean;
    isPublishTimeLocked: boolean;
    scheduleDateTimeRange: ScheduleDateTimeRange | undefined;
    isMultiDateEnabled: boolean;
}

const timezoneOffsetMap = getTimezoneOffsetMap();
// if using global timezone, we can determine if the date is in the past with 100% confidence
// if using local timezone, we can't since we don't have access to timezone offsets across all locations. Besides, the list of locations can change as user selects/deselects them. For now, we consider a date is in the past if it is in the past for the most "behind" locations (most negative UTC offset).
const isScheduleDateInThePast = (scheduleDate: ScheduleDateObject): boolean => {
    let postDateTime;

    if (scheduleDate.useLocationTimezone) {
        postDateTime = convertCalendarDateToDateTime(scheduleDate.postDate).setZone('UTC-12', { keepLocalTime: true });
    } else {
        postDateTime = convertCalendarDateToDateTime(scheduleDate.postDate);
    }

    return postDateTime < DateTime.local();
};
// TODO: consider the time restrain from external source sites for publishing duplicate posts and warn users
const hasDuplicateScheduleDates = (scheduleDates: ScheduleDateObject[]): boolean => {
    // limitation: not able to compare dates scheduled with local timezone vs dates scheduled with global timezone
    const isTwoScheduleDatesSame = (a: ScheduleDateObject, b: ScheduleDateObject): boolean => {
        if (a.useLocationTimezone === b.useLocationTimezone) {
            return (
                convertCalendarDateToDateTime(_.assign({}, a.postDate, { second: 0 })).toMillis() ===
                convertCalendarDateToDateTime(_.assign({}, b.postDate, { second: 0 })).toMillis()
            );
        }
        return false;
    };
    let hasDuplicate = false;

    if (scheduleDates.length > 1) {
        hasDuplicate = scheduleDates.some((a, i) => scheduleDates.findIndex((b) => isTwoScheduleDatesSame(a, b)) !== i);
    }

    return hasDuplicate;
};
const getMinMaxDate = (dateRange?: ScheduleDateTimeRange): { minDate: DateTime; maxDate: DateTime } => {
    let minDate = DateTime.local();
    let maxDate = DateTime.local().plus({ years: 20 });

    if (dateRange && dateRange.minDate && isValidISOstring(dateRange.minDate)) {
        const dateTimeRangeMin = DateTime.fromISO(dateRange.minDate);

        if (dateTimeRangeMin > minDate) {
            minDate = dateTimeRangeMin;
        }
    }
    if (dateRange && dateRange.maxDate && isValidISOstring(dateRange.maxDate)) {
        const dateTimeRangeMax = DateTime.fromISO(dateRange.maxDate);

        if (dateTimeRangeMax > minDate) {
            maxDate = dateTimeRangeMax;
        }
    }

    return { minDate, maxDate };
};
const getDefaultScheduleDate = (minDate: DateTime): ScheduleDateObject => {
    const currentDate = DateTime.local();
    let defaultDate = {
        postDate: convertDateTimeToCalendarDate(currentDate),
        useLocationTimezone: true,
    };

    if (currentDate < minDate) {
        defaultDate = {
            postDate: convertDateTimeToCalendarDate(minDate),
            useLocationTimezone: true,
        };
    }

    return defaultDate;
};

export const DateTimePicker: FunctionComponent<Props> = ({
    publishType,
    scheduleDateTime,
    isSingleLocationUser,
    isPublishTimeLocked,
    scheduleDateTimeRange,
    isMultiDateEnabled,
}) => {
    const dispatch = usePublisherDispatch();
    const { t } = useTranslation(['social/publisher']);
    const { minDate, maxDate } = getMinMaxDate(scheduleDateTimeRange);
    let initialDates: ScheduleDateObject[] = [];

    if (_.isEmpty(scheduleDateTime)) {
        initialDates = [getDefaultScheduleDate(minDate)];
    } else {
        initialDates = scheduleDateTime.map((el) => {
            let newEl = _.cloneDeep(el);
            const lDate = convertCalendarDateToDateTime(el.postDate);

            if (lDate < minDate) {
                newEl.postDate = convertDateTimeToCalendarDate(minDate);
            }
            if (lDate > maxDate) {
                newEl.postDate = convertDateTimeToCalendarDate(maxDate);
            }

            const {
                postDate: { year, month, dayOfMonth, hour, minute, second, timezone },
            } = newEl;

            if (newEl.useLocationTimezone) {
                newEl = {
                    postDate: {
                        year,
                        month,
                        dayOfMonth,
                        hour,
                        minute,
                        second,
                        timezone: formatTimezoneOffset(DateTime.local().offset),
                    },
                    useLocationTimezone: true,
                };
            } else {
                const isTimezoneAvailable = timezoneOffsetMap.findIndex((tz) => tz.offset === timezone) > -1;

                if (!isTimezoneAvailable) {
                    const scheduleDateConvertedToGMT = convertCalendarDateToDateTime(newEl.postDate).setZone('UTC');

                    newEl = {
                        postDate: convertDateTimeToCalendarDate(scheduleDateConvertedToGMT),
                        useLocationTimezone: false,
                    };
                }
            }

            return newEl;
        });
    }

    const [selectedDates, setSelectedDates] = useState(initialDates);

    // warn the user if the date is in the past for the most "ahead" locations
    const shouldShowWarningMessage = (): boolean => {
        let showWarningMessage = false;

        selectedDates.forEach((el) => {
            const { postDate } = el;
            let lDate;

            if (el.useLocationTimezone) {
                lDate = convertCalendarDateToDateTime(postDate).setZone('UTC+14', { keepLocalTime: true });
            } else {
                lDate = convertCalendarDateToDateTime(postDate);
            }

            if (lDate < DateTime.local()) {
                showWarningMessage = true;
            }
        });

        return showWarningMessage;
    };

    const getDateTimeDisplay = (): string => {
        let dateTimeDisplay = '';

        if (initialDates.length > 1) {
            const dateTimeDisplayObject = getScheduleDateTimeDisplayObject(initialDates[0], {
                month: 'long',
                day: 'numeric',
            });

            dateTimeDisplay += `${dateTimeDisplayObject.dateTimeDisplay} + ${t('MORE_TIMES', {
                count: initialDates.length - 1,
            })}`;
        } else {
            const dateTimeDisplayObject = getScheduleDateTimeDisplayObject(initialDates[0]);

            dateTimeDisplay = `${dateTimeDisplayObject.dateTimeDisplay} ${t(dateTimeDisplayObject.timezoneDisplay)}`;
        }

        return dateTimeDisplay;
    };

    const [isModalShowing, setIsModalShowing] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string[]>([]);

    const handleShowModal = (): void => {
        setIsModalShowing(true);
    };
    const handleCloseModal = (): void => {
        setSelectedDates(initialDates);
        setErrorMessage([]);
        setIsModalShowing(false);
    };
    const handleSaveModal = (): void => {
        const errorMessage = [];
        const isAnyScheduleDatesInThePast = selectedDates.some((el) => isScheduleDateInThePast(el));

        if (isAnyScheduleDatesInThePast) {
            errorMessage.push('SCHEDULE_TIME_ERROR_IN_THE_PAST');
        }
        if (hasDuplicateScheduleDates(selectedDates)) {
            errorMessage.push('SCHEDULE_TIME_ERROR_DUPLICATE');
        }

        setErrorMessage(errorMessage);
        if (_.isEmpty(errorMessage)) {
            dispatch({
                type: 'set-schedule-date-time',
                payload: {
                    value: selectedDates,
                },
            });
            setIsModalShowing(false);
        }
    };
    const addScheduleDate = (): void => {
        const newSelectedDates = [...selectedDates, _.cloneDeep(selectedDates[0])];

        setSelectedDates(newSelectedDates);
    };
    const deleteScheduleDate = (index: number): void => {
        const newSelectedDates = [...selectedDates];

        newSelectedDates.splice(index, 1);
        setSelectedDates(newSelectedDates);
        setErrorMessage([]);
    };
    const saveSelectedScheduleDate = (selectedScheduleDate: ScheduleDateObject, index: number): void => {
        const newSelectedDates = [...selectedDates];

        newSelectedDates[index] = selectedScheduleDate;
        setSelectedDates(newSelectedDates);
        setErrorMessage([]);
    };
    const initialDatesString = JSON.stringify(initialDates);

    useEffect((): void => {
        if (publishType === 'PUBLISH_LATER' || publishType === 'DRAFT') {
            dispatch({
                type: 'set-schedule-date-time',
                payload: {
                    value: JSON.parse(initialDatesString),
                },
            });
        } else {
            dispatch({
                type: 'set-schedule-date-time',
                payload: {
                    value: [],
                },
            });
        }
    }, [publishType, dispatch, initialDatesString]);

    return (
        <div className="r4e-social-date-time-picker">
            {(publishType === 'PUBLISH_LATER' || publishType === 'DRAFT') && (
                <Button
                    onClick={(): void => {
                        if (isPublishTimeLocked) {
                            return;
                        }
                        handleShowModal();
                    }}
                    variant={ButtonVariant.Link}
                    className={classnames('date-time-button', { disabled: isPublishTimeLocked })}
                >
                    <i className="icon-calendar" />
                    <span className="data-time-button-text">{getDateTimeDisplay()}</span>
                </Button>
            )}
            <Modal
                show={isModalShowing}
                onHide={handleCloseModal}
                className="r4e-date-time-picker-modal modal-md"
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('PUBLISH_LATER')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="alerts">
                        <Alert variant="warning" show={shouldShowWarningMessage()}>
                            <i className="icon-exclamation-triangle" />
                            <span>{t('SCHEDULE_TIME_WARNING')}</span>
                        </Alert>
                        {errorMessage.map((el) => (
                            <Alert variant="danger" key={el}>
                                <i className="icon-exclamation-circle" />
                                <span>{t(el)}</span>
                            </Alert>
                        ))}
                    </div>
                    <div className="select-date-time">
                        <h6 className="date-time-title">{t('SELECT_DATE_TIME')}</h6>
                        <SingleDateTimePicker
                            selectedScheduleDate={selectedDates[0]}
                            isUseLocationTimezoneEnabled={!isSingleLocationUser}
                            index={0}
                            save={saveSelectedScheduleDate}
                            minDate={minDate}
                            maxDate={maxDate}
                        />
                        {isMultiDateEnabled && (
                            <Button
                                variant={ButtonVariant.Link}
                                onClick={addScheduleDate}
                                className="add-schedule-date-btn"
                            >
                                <i className="icon-plus" /> {t('Add more schedule times')}
                            </Button>
                        )}
                        {selectedDates.length > 1 && (
                            <div className="additional-date-time">
                                <h6 className="additional-date-time-title">{t('ADDITIONAL_DATE_TIME')}</h6>
                                {selectedDates.slice(1).map((el, i) => (
                                    <div className="date-time-picker-container" key={`schedule-date-time-${i + 1}`}>
                                        <SingleDateTimePicker
                                            selectedScheduleDate={el}
                                            isUseLocationTimezoneEnabled={!isSingleLocationUser}
                                            index={i + 1}
                                            save={saveSelectedScheduleDate}
                                            minDate={minDate}
                                            maxDate={maxDate}
                                        />
                                        <Button
                                            variant={ButtonVariant.Icon}
                                            onClick={(): void => deleteScheduleDate(i + 1)}
                                            className="delete-schedule-date-btn"
                                        >
                                            <i className="icon-trash-alt" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant={ButtonVariant.Secondary} onClick={handleCloseModal}>
                        {t('CANCEL')}
                    </Button>
                    <Button variant={ButtonVariant.Primary} onClick={handleSaveModal}>
                        {t('SAVE')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
